import {
    Api,
    ModelRow,
    Navigation,
    StringUtil,
} from "@mcleod/core";
import {
    BlurEvent,
    Button, Checkbox, ClickEvent, DataSourceMode, Layout, LookupModelSelectionEvent, SelectionType, SlideoutDecorator, TableRow,
    TableRowDisplayEvent,
    TableRowMode,
    TableSelectionEvent,
    Textbox
} from "@mcleod/components";
import { AutogenLayoutLocationToProspect } from "./autogen/AutogenLayoutLocationToProspect";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
enum MultiAssignStep {
    ASSIGN_CARRIER = 0,
    SEND_RATE_CON = 1
}

const viewCustomerURL = "lme/ar/Customer?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "customer",
    screenName: "Customer",
    navigateToUrl: viewCustomerURL
}

export class LocationToProspect extends AutogenLayoutLocationToProspect {
    assignSelectedButton: Button;

    static async showAsSlideout(rows: ModelRow[], buttonAssign: Button) {
        const saveButton = new Button({
            id: "buttonAssignCarrier",
            caption: "Convert",
            onClick: async (event: ClickEvent) => {
                if (!layout.mainDataSource.validateSimple()) {
                    return;
                }
                saveButton.busy = true;
                const rowsToPost = layout.tableLocationsCovert.selectedRows.map(row => {
                    return {
                        ...row.data.data
                    };
                });
                const salesperson_id = layout.mainDataSource.activeRow.get("salesperson_id");
                const operations_user = layout.mainDataSource.activeRow.get("operations_user");
                const next_rec_call = layout.mainDataSource.activeRow.get("next_rec_call");
                const prospect_type_id = layout.mainDataSource.activeRow.get("prospect_type_id");
                const mark_plan_id = layout.mainDataSource.activeRow.get("mark_plan_id");
                const rec_call_days = layout.mainDataSource.activeRow.get("rec_call_days");
                const sales_status_id = layout.mainDataSource.activeRow.get("sales_status_id");
                const start_date = layout.mainDataSource.activeRow.get("start_date");
                const response = await Api.post("lme/crm/covert-loc-to-prospect", { locations: rowsToPost,
                    salesperson_id, operations_user, next_rec_call, prospect_type_id, mark_plan_id, rec_call_days,
                    sales_status_id, start_date
                });
                const customerIds = [];
                response.data.forEach((row) => {
                    const customerId = row.id;
                    if (customerId) {
                        customerIds.push(customerId);
                    }
                });
                if (customerIds.length > 0)
                    Navigation.navigateTo("lme/crm/ProspectList?ids=" + customerIds.toString(), { newTab: false });
                saveButton.busy = false;
            },
            backgroundColor: "primary",
            color: "primary.reverse",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        });
        const layout = Layout.getLayout("lme/crm/LocationToProspect") as LocationToProspect;
        layout.assignSelectedButton = saveButton;
        const locations = [];
        for (const row of rows) {
            layout.setLookupModelDisplayValues(row);
            locations.push({
                ...row.data
            });
        }
        buttonAssign.busy = false;
        return new SlideoutDecorator({
            title: "Location to Prospect Conversion",
            layout: layout,
            width: "85%",
            fillVerticalSpace: true,
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                await layout.sourcePreviewLocToProspect.search({ locations });
                layout.updateTitleNumbers();
            },
            onClose: async (cancelled: boolean) => {
                layout.slideOut();
            },
            addlComponents: [saveButton]
        });
    }

    tableLocationsCovertOnRowDisplay(event: TableRowDisplayEvent) {
    const tableRow = event.getTableRow();
    const modelRow = tableRow.data as ModelRow;
    const textboxNewProspectID = tableRow.findComponentById("textboxNewProspectID") as Textbox;
    const checkbox = tableRow.findComponentById("checkboxSelectRow") as Checkbox;
    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
    if (textboxNewProspectID != null) {
        // Track validation states
        let maxLengthValid = true;
        let duplicateCheckValid = true;
        
        const updateRowState = () => {
            const isValid = maxLengthValid && duplicateCheckValid;
            if (isValid) {
                const alreadyUnselectedEnabled = !tableRow.selected && tableRow.enabled;
                checkbox.enabled = true;
                tableRow.enabled = true;
                if (!tableRow.selected && !alreadyUnselectedEnabled) {
                    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
                }
            }
            else {
                checkbox.enabled = false;
                tableRow.enabled = false;
                if (tableRow.selected) {
                    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
                }
            }
            this.updateTitleNumbers();
        };

        // Combined blur listener for both validations
        textboxNewProspectID.addBlurListener(async (event: BlurEvent) => {
            const idToValidate = textboxNewProspectID.getDataValue() || "";
            let validationMessage = "";
            
            // Check max length
            maxLengthValid = idToValidate.length <= 8;
            if (!maxLengthValid) {
                validationMessage = "Maximum length is 8 characters.";
            }
            
            // Check for duplicates if max length is valid and value is not empty
            if (maxLengthValid && !StringUtil.isEmptyString(idToValidate)) {
                try {
                    const response = await Api.post("lme/dispatch/check-duplicate-code", {
                        "id": idToValidate,
                        "table": "customer"
                    });
                    
                    if (response.data[0]?.is_duplicate) {
                        duplicateCheckValid = false;
                        validationMessage = `The code [ ${idToValidate} ] is a duplicate of an existing customer. Please enter a new one.`;
                    } else {
                        duplicateCheckValid = true;
                    }
                } catch (error) {
                    console.error("Error checking duplicate customer ID:", error);
                    duplicateCheckValid = false;
                    validationMessage = "Error validating ID. Please try again.";
                }
            } else if (StringUtil.isEmptyString(idToValidate)) {
                duplicateCheckValid = true;
            }
            
            // Set validation state and styling
            const isValid = maxLengthValid && duplicateCheckValid;
            if (!isValid) {
                textboxNewProspectID.validationWarning = validationMessage;
                textboxNewProspectID.borderColor = "error";
                textboxNewProspectID.showAdhocTooltip(null, { timeout: 5000 });
            } else {
                textboxNewProspectID.validationWarning = null;
                textboxNewProspectID.borderColor = null;
            }
            
            updateRowState();
        });
    }

    const potentialMatches = modelRow.get("potential_matches");
    const textboxProspectToLink = tableRow.findComponentById("textboxProspectToLink") as Textbox;
    if (textboxProspectToLink != null) {
        if (potentialMatches != null && potentialMatches.length > 0) {
            const imageWarning = tableRow.findComponentById("imageWarning");
            if (imageWarning != null) {
                imageWarning.visible = true;
            }
            textboxProspectToLink.addBeforeLookupModelSearchListener((event) => {
                const idFilter = potentialMatches.map(match => encodeURIComponent(match.id)).join(" || ");
                event.filter.near_ids = idFilter;
            });
        }
        else {
            textboxProspectToLink.visible = false;
        }
    }
}

    textboxOperationsUserBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U || E";
    }

    tableLocationsCovertOnSelect(event: TableSelectionEvent) {
        this.updateTitleNumbers();
        this.checkForRequiredSalesperson();
    }

    updateTitleNumbers() {
        this.labelRecordCount.text = this.tableLocationsCovert.selectedRows.length.toString();
        if (this.tableLocationsCovert.selectedRows.length == 0) {
            this.assignSelectedButton.enabled = false;
            this.assignSelectedButton.disabledTooltip = "You must select at least one location to assign.";
        }
        else {
            this.assignSelectedButton.enabled = true;
        }
    }

    checkForRequiredSalesperson() {
    const selectedRows = this.tableLocationsCovert.selectedRows;
    
    // Check if any selected row has has_salesperson set to false
    const requiresSalesperson = selectedRows.some(row => {
        const hasSalesperson = row.data.get("has_salesperson");
        return hasSalesperson === false;
    });
    
    // Set the salesperson field as required or not based on the check
    if (requiresSalesperson) {
        this.textboxSalesperson.required = true;
    } else {
        this.textboxSalesperson.required = false;
    }
}

    setLookupModelDisplayValues(row: ModelRow) {
        row.set("def_commodity_id_display", row.getFirstLookupModelData("def_commodity_id")?.get("descr"));
        row.set("category_display", row.getFirstLookupModelData("category")?.get("descr"));

    }

    textboxProspectToLinkOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textboxProspectToLink = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textboxProspectToLink);
        const textboxNewProspectID = tableRow.findComponentById("textboxNewProspectID") as Textbox;
        if (textboxNewProspectID) {
            if (event.hasSelection() !== true) {
                textboxNewProspectID.enabled = true;    
            }
            else {
                textboxNewProspectID.enabled = false;
                textboxNewProspectID.clear();
                textboxNewProspectID.disabledTooltip = "When a customer is selected to link, a new prospect will not be created so this field is disabled.";
            }
        }   
    }

    textboxNewProspectIDOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        if (textbox) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const textboxProspectToLink = tableRow.findComponentById("textboxProspectToLink") as Textbox;
            const value = textbox.getDataValue();
            if (!StringUtil.isEmptyString(value)) {
                textboxProspectToLink.clear();
                textboxProspectToLink.enabled = false;
                textboxProspectToLink.disabledTooltip = "You may not link a prospect when a new prospect ID to create is entered.";
            }
            else {
                textboxProspectToLink.enabled = true;
            }
        }
    }
}